import useSWR from 'swr';
import { useAuth } from '../contexts/AuthContext';
import { fetcher } from '../services/fetcher';
import type { Client } from '../modules/clients/useData';
import type { ClientContact } from '../modules/client-contacts/useData';

export const ADDRESS_BOOK_ORGS_KEY = '/emails/address-book/organizations';
export const ADDRESS_BOOK_CONTACTS_KEY = '/emails/address-book/contacts';

/** Organizations for compose / link — uses clients module when permitted, otherwise emails address book. */
export const useAddressBookOrganizations = (enabled = true) => {
  const { can, permissionsLoaded } = useAuth();
  const hasClients = permissionsLoaded && can('clients', 'view');
  const url =
    enabled && permissionsLoaded
      ? hasClients
        ? '/crm-clients'
        : ADDRESS_BOOK_ORGS_KEY
      : null;

  const { data, error, isLoading, mutate } = useSWR<Client[]>(url, fetcher, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
  });

  const organizations = Array.isArray(data) ? data : [];
  return {
    data: organizations,
    organizations,
    isLoading: isLoading || !permissionsLoaded,
    isError: error,
    mutate,
  };
};

/** Contacts for compose address book — uses clients module when permitted, otherwise emails address book. */
export const useAddressBookContacts = (enabled = true, searchParams?: string) => {
  const { can, permissionsLoaded } = useAuth();
  const hasClients = permissionsLoaded && can('clients', 'view');
  const queryString = searchParams ? `?searchParams=${encodeURIComponent(searchParams)}` : '';
  const url =
    enabled && permissionsLoaded
      ? hasClients
        ? `/client-contacts${queryString}`
        : `${ADDRESS_BOOK_CONTACTS_KEY}${queryString}`
      : null;

  const { data, error, isLoading, mutate } = useSWR<ClientContact[]>(url, fetcher, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
  });

  const contacts = Array.isArray(data) ? data : [];
  return {
    data: contacts,
    contacts,
    isLoading: isLoading || !permissionsLoaded,
    isError: error,
    mutate,
  };
};
